// Import everything from autoload folder
; // eslint-disable-line
import { initNavigation } from './modules/navigation';

// Import local dependencies
import { Fancybox } from '@fancyapps/ui';
import { initSliders } from './modules/slider';
import { initScrollAnimations } from './modules/animations';
import { initCountUp } from './modules/count-up';
import { initTestimonials } from './modules/testimonials';
import { initVideos } from './modules/video';
import { initSmoothScroll, getSmoothScroll } from './modules/smooth-scroll';

const DESKTOP_BREAKPOINT = 991;

function prefersReducedMotion() {
  return (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function smoothScrollToY(top) {
  const lenis = getSmoothScroll();
  if (lenis && !prefersReducedMotion()) {
    lenis.scrollTo(Math.max(0, top));
    return;
  }
  window.scrollTo({
    top: Math.max(0, top),
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

/**
 * Bind Fancybox to gallery items and lightbox selectors
 */
function initFancybox(scope = document) {
  const galleryLinks = scope.querySelectorAll(
    '.gallery-item a[href$="jpg"], .gallery-item a[href$="png"], .gallery-item a[href$="gif"], .gallery-item a[href$="webp"]'
  );
  galleryLinks.forEach((link) => {
    link.setAttribute('rel', 'gallery');
    link.setAttribute('data-fancybox', 'gallery');
  });

  Fancybox.bind(scope, '[data-fancybox], .fancybox, a[rel*="album"]', {
    on: {
      init: () => getSmoothScroll()?.stop(),
      close: () => getSmoothScroll()?.start(),
    },
  });
}

/**
 * Gravity Forms confirmation scroll and validation message toggles
 */
function initGravityForms() {
  const scrollToConfirmation = (formId) => {
    if (!formId) return;
    const target = document.getElementById(
      `gform_confirmation_wrapper_${formId}`
    );
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 50;
      smoothScrollToY(top);
    }
  };

  document.addEventListener('gform/confirmation_loaded', (event) => {
    const formId = event.detail && event.detail.formId;
    scrollToConfirmation(formId);
  });

  if (window.jQuery) {
    window.jQuery(document).on('gform_confirmation_loaded', (event, formId) => {
      scrollToConfirmation(formId);
    });
  }

  const handleFieldInput = (event) => {
    const target = event.target;
    if (
      !target ||
      typeof target.matches !== 'function' ||
      !target.matches('.gfield input, .gfield textarea, .gfield select')
    ) {
      return;
    }

    const field = target.closest('.gfield');
    if (!field || !field.classList.contains('gfield_error')) {
      return;
    }

    const validationMessage = field.querySelector('.validation_message');
    if (!validationMessage) {
      return;
    }

    const hasValue = Boolean(target.value && target.value.length);
    validationMessage.style.display = hasValue ? 'none' : '';
  };

  document.body.addEventListener('input', handleFieldInput);
  document.body.addEventListener('change', handleFieldInput);
}

/**
 * Update body scrolling state classes
 */
let lastScrollTop = 0;
function updateScrollClasses() {
  const st = window.scrollY || document.documentElement.scrollTop || 0;
  document.body.classList.toggle('scrolling-up', st < lastScrollTop);
  document.body.classList.toggle('scrolling-down', st > lastScrollTop);
  document.body.classList.toggle('scrolling', st !== 0);
  document.body.classList.toggle('is-scrolled', st > 10);
  document.body.classList.toggle(
    'scroll-down',
    st > lastScrollTop &&
      st > 400 &&
      document
        .querySelector('.navbar-toggler')
        ?.getAttribute('aria-expanded') !== 'true'
  );

  if (st === 0) {
    document.body.classList.remove('scrolling-up', 'scrolling-down');
  }
  lastScrollTop = st;
}

/**
 * Resolve an anchor hash safely without throwing SyntaxError on malformed selectors
 */
function findAnchorTarget(hash, sourceLink = null) {
  if (!hash || hash === '#' || !hash.startsWith('#')) {
    return null;
  }

  if (hash === '#next' && sourceLink) {
    const currentSection = sourceLink.closest('section');
    let nextEl = currentSection ? currentSection.nextElementSibling : null;
    while (nextEl && nextEl.tagName !== 'SECTION') {
      nextEl = nextEl.nextElementSibling;
    }
    return nextEl;
  }

  let rawId = hash.slice(1);
  if (!rawId) return null;

  try {
    rawId = decodeURIComponent(rawId);
  } catch (e) {
    // Keep rawId as-is if percent-encoding is malformed
  }

  const byId = document.getElementById(rawId);
  if (byId) {
    return byId;
  }

  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    try {
      return document.querySelector(`#${CSS.escape(rawId)}`);
    } catch (e) {
      return null;
    }
  }

  return null;
}

/**
 * Scrolling to anchor links and initial URL hash
 */
function scrollToAnchor() {
  const getTopOffset = () =>
    window.innerWidth > DESKTOP_BREAKPOINT ? 100 : 70;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (
      !href ||
      href === '#' ||
      link.classList.contains('fancybox') ||
      link.hasAttribute('data-fancybox') ||
      link.getAttribute('role') === 'tab'
    ) {
      return;
    }

    const target = findAnchorTarget(href, link);
    if (target) {
      event.preventDefault();
      const top =
        target.getBoundingClientRect().top + window.scrollY - getTopOffset();
      smoothScrollToY(top);
    }
  });

  if (window.location.hash) {
    const target = findAnchorTarget(window.location.hash);
    if (target) {
      const top =
        target.getBoundingClientRect().top + window.scrollY - getTopOffset();
      smoothScrollToY(top);
    }
  }
}

/**
 * Initialize theme scripts on DOMContentLoaded
 */
function initTheme() {
  initSmoothScroll();
  initFancybox();
  initGravityForms();
  initNavigation();
  updateScrollClasses();
  initSliders();
  initScrollAnimations();
  initCountUp();
  initTestimonials();
  initVideos();
  scrollToAnchor();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTheme);
} else {
  initTheme();
}

window.addEventListener('scroll', updateScrollClasses, { passive: true });
